import React, { FC, useCallback, useMemo } from "react";
import { PieCenterLabel, useSxMergeStrict } from "shared-ts-mui";
import {
  DefaultizedPieValueType,
  pieArcLabelClasses,
  PieChart,
} from "@mui/x-charts";
import { useTranslation } from "react-i18next";
import { SxProps, useTheme } from "@mui/material";
import { PieValueType } from "@mui/x-charts/models";

export interface WastePieChartProps {
  unit: string;
  wasteAmount: number;
  creosoteAmount: number;
  sootAmount: number;
  ashAmount: number;
  sx?: SxProps;
}

export const WastePieChart: FC<WastePieChartProps> = (props) => {
  const { t } = useTranslation("firefly-chimney-insights-ts-domain");
  const theme = useTheme();
  const sootInWasteUnit = useMemo(
    () => (props.wasteAmount / 100) * props.sootAmount,
    [props.sootAmount, props.wasteAmount]
  );
  const ashInWasteUnit = useMemo(
    () => (props.wasteAmount / 100) * props.ashAmount,
    [props.ashAmount, props.wasteAmount]
  );
  const creosoteInWasteUnit = useMemo(
    () => (props.wasteAmount / 100) * props.creosoteAmount,
    [props.creosoteAmount, props.wasteAmount]
  );

  const valueFormatter = useCallback(
    (item: { value: number }) => `${item.value.toFixed(1)} ${props.unit}`,
    [props.unit]
  );

  const getArcLabel = useCallback(
    (params: DefaultizedPieValueType) => {
      return `${params.value.toFixed(1)} ${props.unit}`;
    },
    [props.unit]
  );

  const data: PieValueType[] = useMemo(() => {
    const data: PieValueType[] = [];
    if (creosoteInWasteUnit > 0) {
      data.push({
        id: "creosote",
        label: t("Creosote"),
        value: creosoteInWasteUnit,
        color: theme.palette.error.main,
      });
    }
    if (sootInWasteUnit > 0) {
      data.push({
        id: "soot",
        label: t("Soot"),
        value: sootInWasteUnit,
        color: theme.palette.warning.main,
      });
    }
    if (ashInWasteUnit > 0) {
      data.push({
        id: "ash",
        label: t("Ash"),
        value: ashInWasteUnit,
        color: theme.palette.grey["500"],
      });
    }
    return data;
  }, [
    ashInWasteUnit,
    creosoteInWasteUnit,
    sootInWasteUnit,
    t,
    theme.palette.error.main,
    theme.palette.grey,
    theme.palette.warning,
  ]);

  const sx = useSxMergeStrict(
    () => [
      {
        [`& .${pieArcLabelClasses.root}`]: {
          fontSize: 12,
        },
      },
      props.sx,
    ],
    [props.sx]
  );
  return (
    <PieChart
      series={[
        {
          innerRadius: "50%",
          outerRadius: "100%",
          startAngle: -180,
          endAngle: 180,
          arcLabel: getArcLabel,
          valueFormatter: valueFormatter,
          data: data,
        },
      ]}
      sx={sx}
    >
      <PieCenterLabel>{`${props.wasteAmount} ${props.unit}`}</PieCenterLabel>
    </PieChart>
  );
};
